import { useState } from "react";

import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckIcon from "@mui/icons-material/Check";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import RepeatIcon from "@mui/icons-material/Repeat";
import { useMediaQuery, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import DialogContent from "@mui/material/DialogContent";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { List as LinqList } from "linqts";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import YourOSHCLogo from "../../assets/logos/logo-your-oshc.svg";
import { ButtonPrimary } from "../../components/Common/Buttons/Buttons";
import Center from "../../components/Common/Center";
import { AvatarDisplay } from "../../components/Common/Controls/AvatarDisplay";
import { AnimatedDialog } from "../../components/Common/Dialogs/AnimatedDialog";
import LoadingDialog from "../../components/Common/Dialogs/LoadingDialog";
import Spacer from "../../components/Common/Spacers/Spacers";
import { CancelRolloverDialog } from "../../components/RollOver/CancelRolloverDialog";
import { ScheduleEditorDialog } from "../../components/RollOver/ScheduleEditorDialog";
import { CardLayout } from "../../layouts/Layouts";
import { rollOverSchedulesOpt } from "../../store/modules/rollOver/rollOverActions";
import { clearAddChildData } from "../../store/modules/rollOver/rollOverStateActions";
import {
  RollOverScheduleHttpResponse,
  RollOverStatusHttpRequest,
  RollOverStatusHttpResponse,
} from "../../store/parentApi";
import { RootState } from "../../store/reducers";
import { Child, EntityType } from "../../types/models";
import { IRollOverConfiguration } from "../../types/types";
import { getStyleColor } from "../../utils/helpers";

const useStyles = makeStyles()((theme) => {
  return {
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: "white",
    },
    h1: {
      marginBottom: 0,
    },
    caption: {
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "auto",
      padding: "10px",
      backgroundColor: "rgba(255,255,255,.8)",
      color: "black",
      fontSize: "16px",
      lineHeight: "20px",
      minHeight: "40px",
      maxHeight: "180px",
      overflow: "auto",
    },
    childName: {
      wordBreak: "break-all",
      overflowWrap: "anywhere",
    },
    description: {
      lineHeight: "1.5",
    },
    icon: {
      width: "1.3rem",
      height: "1.3rem",
      paddingRight: "10px",
    },
    logo: {
      height: 50,
      "@media screen and (min-width: 720px)": {
        height: 40,
      },
    },
    section: {
      border: "2px solid var(--youroshc-color-primary)",
      borderRadius: "10px",
    },
    sectionOptIn: {
      border: "2px solid var(--custom-color-red)",
      borderRadius: "10px",
    },
    sectionOptOut: {
      border: "2px solid var(--custom-color-green-dark)",
      borderRadius: "10px",
    },
  };
});

function RollOver() {
  const theme = useTheme();
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [showSaveResultMsg, setShowSaveResultMsg] = useState(false);
  const [savingChildMsg, setSavingChildMsg] = useState("");
  const customerAccountId = useSelector((state: RootState) => state.customer.customer?.customerAccountId);
  const rollOverShowAddChild = useSelector((state: RootState) => state.appConfigs.rollOverShowAddChild);
  const children = useSelector((state: RootState) => state.child.childrenByCustomer);

  const avatars = useSelector((state: RootState) =>
    state.avatars?.avatars?.filter((x) => x.entityType === EntityType.Child)
  );
  const commonData = useSelector((state: RootState) => state.commonData);
  const { rollOverSchedules, isSaving, isSaved, isSavingError } = useSelector(
    (state: RootState) => state.rollOverReducer
  );

  const childrenWithoutSchedule =
    children?.filter((child) => !(rollOverSchedules?.some((s) => s.childId === child.childId) ?? false)) ?? [];

  var nextYear = dayjs().add(1, "year");
  var now = dayjs();
  const rollOverConfiguration =
    commonData.isInitialised && commonData.data !== null && commonData.data.rollOverConfiguration !== undefined
      ? commonData.data.rollOverConfiguration
      : undefined;

  const rollOverIsActive =
    rollOverConfiguration !== undefined &&
    rollOverConfiguration.forYear === nextYear.year() &&
    now.isBetween(rollOverConfiguration.optFrom, rollOverConfiguration.optTo)
      ? true
      : false;

  const [scheduleEditorPopUpOpen, setScheduleEditorPopUpOpen] = useState(false);
  const [scheduleEditorData, setScheduleEditorData] = useState<RollOverScheduleHttpResponse[]>([]);

  const [cancelRollOverPopUpOpen, setCancelRollOverPopUpOpen] = useState(false);
  const [cancelRollOverData, setCancelRollOverData] = useState<RollOverScheduleHttpResponse[]>([]);

  const handleScheduleEditorPopUpOpen = (serviceSchedules: RollOverScheduleHttpResponse[]) => {
    setSavingChildMsg(serviceSchedules[0].firstName!);
    setScheduleEditorData(serviceSchedules.sort((a, b) => (a.careTypeId % 100) - (b.careTypeId % 100)));
    setScheduleEditorPopUpOpen(true);
  };

  const handleScheduleEditorPopUpClose = (showSaved: boolean) => {
    if (showSaved) {
      setShowSaveResultMsg(true);
    }
    setScheduleEditorPopUpOpen(false);
    dispatch(clearAddChildData());
  };

  const handleCancelRollOverPopUpOpen = (serviceSchedules: RollOverScheduleHttpResponse[]) => {
    setSavingChildMsg(serviceSchedules[0].firstName!);
    setCancelRollOverData(serviceSchedules);
    setCancelRollOverPopUpOpen(true);
  };

  const handleCancelRollOverPopUpClose = (showSaved: boolean) => {
    if (showSaved) {
      setShowSaveResultMsg(true);
    }
    setCancelRollOverPopUpOpen(false);
  };

  function onToggleRollOverScheduleOpt(
    childId: number,
    rollIds: number[],
    rollOverStatus: RollOverStatusHttpResponse,
    child: Child
  ) {
    setSavingChildMsg(child.firstName!);
    var opt =
      rollOverStatus === RollOverStatusHttpResponse.OptOut
        ? RollOverStatusHttpRequest.OptOut
        : RollOverStatusHttpRequest.OptIn;
    rollOverSchedulesOpt(dispatch, customerAccountId!, childId, rollIds, opt, () => {
      setShowSaveResultMsg(true);
    });
  }

  const groupedByStatus = new LinqList<RollOverScheduleHttpResponse>(rollOverSchedules ?? []).GroupBy(
    (x) => {
      if (x.rollOverStatus === RollOverStatusHttpResponse.Undefined) {
        return x.cohortStatus === RollOverStatusHttpResponse.OptOut
          ? RollOverStatusHttpResponse.OptOut
          : RollOverStatusHttpResponse.Undefined;
      }

      if (x.rollOverStatus === RollOverStatusHttpResponse.OptOut) {
        return RollOverStatusHttpResponse.OptOut;
      }

      return RollOverStatusHttpResponse.OptIn;
    },
    (x) => x
  );

  if (rollOverConfiguration === undefined) return <></>;
  return (
    <CardLayout>
      <CardLayout.Header bgColor="white">
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs>
            <h1 className={`h2 ${classes.h1}`}>My {rollOverConfiguration.forYear} Schedule</h1>
          </Grid>
        </Grid>
      </CardLayout.Header>
      <CardLayout.Body style={{ paddingTop: 0 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LoadingDialog open={isSaving} />
            {Object.keys(groupedByStatus).length > 0 && (
              <>
                <Grid container sx={{ bgcolor: "background.paper" }}>
                  <Grid item xs={12}>
                    <h3>Congratulations!</h3>
                    <p className={classes.description}>
                      Thanks for being a loyal Camp Australia family. Below are the Before and/or After School Care
                      schedules we've found for your child/ren. We've already locked in some schedules that appear to be
                      heavily used, and for others, you now have the option to secure them early.
                      <br />
                      <br />
                      Please review the details below and confirm, edit, or cancel your schedules by{" "}
                      <strong>{rollOverConfiguration.optTo.format("DD MMMM YYYY")}</strong>. Bookings for{" "}
                      {rollOverConfiguration.forYear} will officially be processed from{" "}
                      <strong>{rollOverConfiguration.bookingsOpenOn.format("DD MMMM YYYY")}</strong>, and we'll notify
                      you once they're confirmed.
                      <br />
                      <br />
                      While we make every effort to secure all requested bookings, there is a chance that some schedules
                      may be impacted by space and licensing limitations. In these cases, we'll contact you as soon as
                      possible to discuss options.
                      <br />
                      <br />
                      Let us know your schedule preferences now to plan ahead with confidence - and enjoy peace of mind
                      knowing you're one of the first in line for {rollOverConfiguration.forYear}!
                    </p>
                  </Grid>
                </Grid>
                <AnimatedDialog
                  open={showSaveResultMsg}
                  onClose={() => {
                    setShowSaveResultMsg(false);
                  }}
                >
                  <DialogContent>
                    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", pt: 0 }}>
                      {isSaved && (
                        <Center>
                          <CheckCircleIcon style={{ color: "var(--color-primary)", fontSize: "3rem" }} />
                        </Center>
                      )}
                      {isSavingError && (
                        <Center>
                          <ErrorIcon style={{ color: "var(--custom-color-error)", fontSize: "3rem" }} />
                        </Center>
                      )}
                      <Spacer margin={10} />
                      <Typography
                        gutterBottom
                        sx={{ color: isSaved ? "#1d4e5c" : "var(--custom-color-error)", fontWeight: "bold" }}
                      >
                        <Center>
                          {isSaved ? "Thank you" : "Error while trying save your changes. Please try again later."}
                        </Center>
                      </Typography>
                    </Box>
                  </DialogContent>
                </AnimatedDialog>
              </>
            )}
            {rollOverIsActive && Object.keys(groupedByStatus).length > 0 ? (
              <>
                {rollOverShowAddChild && (
                  <Grid container paddingBottom={1}>
                    <ButtonPrimary
                      style={{
                        backgroundColor: "#FF6933",
                        color: "white",
                        fontWeight: "bold",
                      }}
                      onClick={() => {
                        setScheduleEditorData([]);
                        setScheduleEditorPopUpOpen(true);
                      }}
                    >
                      Add Child
                    </ButtonPrimary>
                  </Grid>
                )}
                {renderSchedules(RollOverStatusHttpResponse.Undefined)}
                {renderSchedules(RollOverStatusHttpResponse.OptIn)}
                {renderSchedules(RollOverStatusHttpResponse.OptOut)}
              </>
            ) : (
              <span>No bookings</span>
            )}
            <Grid>
              <span className="small">
                * Please note that while we strive to secure all bookings, there is a small chance (around 1%) that some
                schedules may be impacted due to space and licensing limitations.
              </span>
            </Grid>
          </Grid>
        </Grid>

        {scheduleEditorPopUpOpen && (
          <ScheduleEditorDialog
            handleClose={handleScheduleEditorPopUpClose}
            open={scheduleEditorPopUpOpen}
            schedules={scheduleEditorData}
            year={rollOverConfiguration.forYear}
            isNew={scheduleEditorData.length === 0}
            childrenWithoutSchedule={childrenWithoutSchedule}
          />
        )}

        {cancelRollOverPopUpOpen && (
          <CancelRolloverDialog
            handleClose={handleCancelRollOverPopUpClose}
            open={cancelRollOverPopUpOpen}
            schedules={cancelRollOverData}
            year={rollOverConfiguration.forYear}
          />
        )}
      </CardLayout.Body>
    </CardLayout>
  );

  function renderSchedules(rollOverStatusFilter: RollOverStatusHttpResponse) {
    const filteredByStatus = groupedByStatus[rollOverStatusFilter];

    const servicesByChildStatus = new LinqList<RollOverScheduleHttpResponse>(filteredByStatus ?? []).GroupBy(
      (x) => `${x.childId}-${x.serviceId}`
    );

    if (Object.keys(servicesByChildStatus).length === 0) {
      return <></>;
    }

    return (
      <Grid container>
        <Grid paddingBottom={1}>
          <strong>
            {rollOverStatusFilter === RollOverStatusHttpResponse.Undefined
              ? "Pending Action"
              : rollOverStatusFilter === RollOverStatusHttpResponse.OptOut
              ? "Secured"
              : "Cancelled"}
          </strong>
        </Grid>
        {Object.keys(servicesByChildStatus).map((cs, index) => {
          var serviceSchedules = servicesByChildStatus[cs];
          var child = children?.find((x) => x.childId === serviceSchedules[0].childId);
          var avatar = avatars.find((x) => x.entityId === child?.childId?.toString());
          var rollOverStatus = serviceSchedules[0].rollOverStatus;

          var cohortStatus = serviceSchedules[0].cohortStatus;
          var isOptOut =
            rollOverStatus === RollOverStatusHttpResponse.OptOut ||
            (rollOverStatus === RollOverStatusHttpResponse.Undefined &&
              cohortStatus === RollOverStatusHttpResponse.OptOut);

          var cardBackgroundColor =
            rollOverStatusFilter === RollOverStatusHttpResponse.OptOut
              ? "var(--custom-color-green-light)!important"
              : rollOverStatusFilter === RollOverStatusHttpResponse.OptIn
              ? "var(--custom-color-light-red)!important"
              : "";
          var cardClassName =
            rollOverStatusFilter === RollOverStatusHttpResponse.OptOut
              ? classes.sectionOptOut
              : rollOverStatusFilter === RollOverStatusHttpResponse.OptIn
              ? classes.sectionOptIn
              : classes.section;

          return (
            <>
              <Grid item key={index} xs={12}>
                <Card
                  className={cardClassName}
                  sx={{
                    backgroundColor: cardBackgroundColor,
                  }}
                >
                  <CardContent>
                    <Grid
                      container
                      direction="row"
                      pl="15px"
                      pt={2}
                      spacing={2}
                      justifyContent="flex-start"
                      alignItems="start"
                    >
                      <Grid item xl={1} xs={12}>
                        <Grid container direction={"row"} justifyContent={"flex-start"} alignItems="center">
                          <Grid item pr="10px">
                            <Box
                              sx={{
                                width: "8px",
                                alignContent: "center",
                                height: "50px",
                                alignItems: "center",
                                backgroundColor: "var(--youroshc-color-primary)!important",
                                borderTopRightRadius: 5,
                                borderBottomRightRadius: 5,
                                borderTopLeftRadius: 5,
                                borderBottomLeftRadius: 5,
                              }}
                            ></Box>
                          </Grid>

                          <Grid item>
                            <Grid container direction="column" alignItems="flex-start">
                              <img src={YourOSHCLogo} alt="Your OSHC Logo" className={classes.logo} />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xl={2} xs={12}>
                        <Grid container direction="column" alignItems="start" gap={1}>
                          <Grid container direction="row" columnGap={1} alignItems="center">
                            <PersonOutlineIcon fontSize="small" className={classes.icon} />
                            <Stack
                              direction="row"
                              gap={1}
                              alignItems="center"
                              divider={<Divider orientation="vertical" flexItem />}
                              spacing={2}
                            >
                              <Grid container direction="row" gap={1} alignItems="center">
                                <AvatarDisplay info={avatar || { index: child?.avatarIndex }} size={25} />
                                <Typography
                                  fontSize={{
                                    md: "14px",
                                    xs: "12px",
                                  }}
                                  className={classes.childName}
                                >
                                  {`${child?.firstName} ${child?.lastName?.substring(0, 1)}`}
                                </Typography>
                              </Grid>
                            </Stack>
                          </Grid>
                          <Spacer margin={5} />
                          <Grid container direction="row" columnGap={1} alignItems="center">
                            <LocationOnOutlinedIcon className={classes.icon} />
                            <Typography
                              fontSize={{
                                md: "14px",
                                xs: "12px",
                              }}
                            >
                              {serviceSchedules[0].serviceName}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item xl={3} xs={12} pt={0}>
                        {serviceSchedules
                          .sort((a, b) => (a.careTypeId % 100) - (b.careTypeId % 100))
                          .map((sch) => {
                            if (sch.frequencyTypeId === 1) {
                              return (
                                <>
                                  <Grid container>
                                    <RepeatIcon className={classes.icon} />
                                    <Typography variant="subtitle2" fontWeight={"bold"}>
                                      {sch.rollName}
                                    </Typography>
                                  </Grid>

                                  <Grid container>
                                    {getScheduleDescription(rollOverStatus, "Occurs every ", getDays(sch, 1))}
                                  </Grid>
                                  <Spacer margin={5} />
                                </>
                              );
                            }

                            var daysWeek1 = getDays(sch, 1);
                            var daysWeek2 = getDays(sch, 2);
                            return (
                              <>
                                <Grid container>
                                  <RepeatIcon className={classes.icon} />
                                  <Typography variant="subtitle2" fontWeight={"bold"}>
                                    {sch.rollName}
                                  </Typography>
                                </Grid>
                                <Grid container>
                                  {daysWeek1.length > 0 &&
                                    getScheduleDescription(rollOverStatus, "Week A: Occurs every ", getDays(sch, 1))}
                                </Grid>
                                <Grid container>
                                  {daysWeek2.length > 0 &&
                                    getScheduleDescription(rollOverStatus, "Week B: Occurs every ", getDays(sch, 2))}
                                </Grid>
                                <Spacer margin={5} />
                              </>
                            );
                          })}
                      </Grid>
                      <Grid item xl={6} xs={12} sx={{ paddingTop: "0px !important" }}>
                        <Grid container alignItems="center">
                          {isXl ? (
                            <Grid item xs={12}>
                              <Box display="flex" justifyContent="flex-end" gap={2} alignItems="center">
                                {renderCancellation(
                                  rollOverStatus,
                                  handleCancelRollOverPopUpOpen,
                                  serviceSchedules,
                                  rollOverConfiguration!
                                )}
                                {renderEdit(handleScheduleEditorPopUpOpen, serviceSchedules)}
                                {renderSecure(
                                  rollOverStatus,
                                  isOptOut,
                                  onToggleRollOverScheduleOpt,
                                  serviceSchedules,
                                  child,
                                  rollOverConfiguration!
                                )}
                              </Box>
                            </Grid>
                          ) : (
                            <>
                              <Grid item xs={12} style={{ height: "100%" }}>
                                {renderCancellation(
                                  rollOverStatus,
                                  handleCancelRollOverPopUpOpen,
                                  serviceSchedules,
                                  rollOverConfiguration!
                                )}
                              </Grid>
                              <Grid item xs={12} style={{ height: "100%" }}>
                                {renderEdit(handleScheduleEditorPopUpOpen, serviceSchedules)}
                              </Grid>
                              <Grid item xs={12} style={{ height: "100%" }}>
                                {renderSecure(
                                  rollOverStatus,
                                  isOptOut,
                                  onToggleRollOverScheduleOpt,
                                  serviceSchedules,
                                  child,
                                  rollOverConfiguration!
                                )}
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Spacer margin={10} />
            </>
          );
        })}
      </Grid>
    );
  }

  function getScheduleDescription(rollOverStatus: RollOverStatusHttpResponse, daysTitle: string, days: string[]) {
    if (!days.length) {
      return (
        <Typography variant="subtitle2" fontSize={{ md: "14px", xs: "12px" }} sx={{ color: "text.secondary" }}>
          No days scheduled
        </Typography>
      );
    }

    const isOptedIn = rollOverStatus === RollOverStatusHttpResponse.OptIn;

    return (
      <Typography
        variant="subtitle2"
        fontSize={{ md: "14px", xs: "12px" }}
        sx={{ textDecoration: isOptedIn ? "line-through" : "none" }}
      >
        {renderDays(daysTitle, days)}
      </Typography>
    );
  }

  function getDays(rollOverSchedule: RollOverScheduleHttpResponse, week: 1 | 2) {
    const dayMap: Record<"mon" | "tue" | "wed" | "thu" | "fri", string> = {
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
    };

    return (Object.keys(dayMap) as Array<keyof typeof dayMap>)
      .filter((day) => rollOverSchedule[`${day}${week}` as keyof RollOverScheduleHttpResponse])
      .map((day) => dayMap[day]);
  }

  function renderDays(title: string | null, days: string[]) {
    var lastDay = days.pop();

    return (
      <>
        {title && title}
        {days.map((day, index) => (
          <>
            {index > 0 && index < days.length && ", "}
            {day}
          </>
        ))}
        {days.length > 0 && <>&nbsp;and</>}
        <>
          &nbsp;
          {lastDay}
        </>
      </>
    );
  }
}

function renderSecure(
  rollOverStatus: RollOverStatusHttpResponse,
  isOptOut: boolean,
  onToggleRollOverScheduleOpt: (
    childId: number,
    rollIds: number[],
    rollOverStatus: RollOverStatusHttpResponse,
    child: Child
  ) => void,
  serviceSchedules: RollOverScheduleHttpResponse[],
  child: Child | undefined,
  rollOverConfiguration: IRollOverConfiguration
) {
  return (
    <>
      {(rollOverStatus === RollOverStatusHttpResponse.Undefined ||
        rollOverStatus === RollOverStatusHttpResponse.OptIn) &&
        !isOptOut && (
          <ButtonPrimary
            style={{
              backgroundColor: getStyleColor("--color-primary"),
              color: "black",
              fontWeight: "bold",
              paddingLeft: "10px",
              paddingRight: "10px",
            }}
            sx={{
              marginLeft: { lg: "5px", xs: "0px" },
            }}
            onClick={() =>
              onToggleRollOverScheduleOpt(
                serviceSchedules[0].childId,
                serviceSchedules.map((x) => x.rollId),
                RollOverStatusHttpResponse.OptOut,
                child!
              )
            }
          >
            {`Secure my ${rollOverConfiguration.forYear} schedule`}
          </ButtonPrimary>
        )}
      {isOptOut && (
        <Stack direction="row" spacing={1} alignItems={"center"}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: getStyleColor("--color-primary"),
              border: "2px solid black",
            }}
          >
            <CheckIcon sx={{ color: "black", fontSize: 20 }} />
          </Box>
          <Typography fontSize={"16px"} fontWeight="bold">
            {`${rollOverConfiguration.forYear} Schedule Secured *`}
          </Typography>
        </Stack>
      )}
    </>
  );
}

function renderEdit(
  handleScheduleEditorPopUpOpen: (serviceSchedules: RollOverScheduleHttpResponse[]) => void,
  serviceSchedules: RollOverScheduleHttpResponse[]
) {
  return (
    <>
      <ButtonPrimary
        style={{
          backgroundColor: "white",
          color: "black",
          fontWeight: "bold",
        }}
        onClick={() => handleScheduleEditorPopUpOpen(serviceSchedules)}
      >
        Edit schedule
      </ButtonPrimary>
    </>
  );
}

function renderCancellation(
  rollOverStatus: RollOverStatusHttpResponse,
  handleCancelRollOverPopUpOpen: (serviceSchedules: RollOverScheduleHttpResponse[]) => void,
  serviceSchedules: RollOverScheduleHttpResponse[],
  rollOverConfiguration: IRollOverConfiguration
) {
  return (
    <>
      {(rollOverStatus === RollOverStatusHttpResponse.OptOut ||
        rollOverStatus === RollOverStatusHttpResponse.Undefined) && (
        <Link
          // eslint-disable-next-line no-script-url
          href="javascript:void(0)"
          sx={{ color: "black", textDecorationColor: "black", fontWeight: "bold" }}
          onClick={() => handleCancelRollOverPopUpOpen(serviceSchedules)}
        >
          {`I don't want my ${rollOverConfiguration.forYear} schedule`}
        </Link>
      )}
      {rollOverStatus === RollOverStatusHttpResponse.OptIn && (
        <Stack direction="row" spacing={1} alignItems={"center"}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: 20,
              height: 20,
              borderRadius: "50%",
              color: "red",
            }}
          >
            <CancelRoundedIcon sx={{ color: "var(--custom-color-error)", fontSize: 22 }} />
          </Box>
          <Typography fontSize={"16px"} fontWeight="bold">
            {`Cancelled ${rollOverConfiguration.forYear} Schedule`}
          </Typography>
        </Stack>
      )}
    </>
  );
}

export default RollOver;
